import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { createTheme, styled, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Button from '@mui/material/Button';

import ListItems from '../layout/ListItems';
import { useRecoilState } from 'recoil';
import { authAtom } from '../_state';
import Paper from '@mui/material/Paper';

import logo from '../assets/img/footer_logo.png';
import Stack from '@mui/material/Stack';

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    boxSizing: 'border-box',
    ...(!open && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9)
      }
    })
  }
}));

const mdTheme = createTheme({
  palette: {
    primary: {
      main: '#004442'
    }
  }
});

function DashboardContent() {
  const navigate = useNavigate();

  const [open, setOpen] = useState(true);
  const [auth, setAuth] = useRecoilState(authAtom);

  const handleLogout = (e) => {
    Cookies.remove('access_token');
    Cookies.remove('refresh_token');
    alert('로그아웃이 완료 되었습니다.');
    localStorage.clear();
    setAuth(null);
    navigate('/login');
  };

  const toggleDrawer = () => {
    setOpen(!open);
  };

  return (
    <ThemeProvider theme={mdTheme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position="absolute" open={open}>
          <Toolbar
            sx={{
              pr: '24px' // keep right padding when drawer closed
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={toggleDrawer}
              sx={{
                marginRight: '36px',
                ...(open && { display: 'none' })
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography component="h1" variant="h6" color="inherit" noWrap sx={{ flexGrow: 1 }}>
              도화 앱스 (Dohwa Apps)
            </Typography>
            <Button variant="text" sx={{ color: 'white' }} onClick={handleLogout}>
              로그아웃
            </Button>
          </Toolbar>
        </AppBar>
        <Drawer variant="permanent" open={open}>
          <Toolbar
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              px: [1]
            }}
          >
            <IconButton onClick={toggleDrawer}>
              <ChevronLeftIcon />
            </IconButton>
          </Toolbar>
          <ListItems />
        </Drawer>
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900]),
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto'
          }}
        >
          <Toolbar />
          <Container maxWidth="100%" sx={{ mt: 4, mb: 4 }}>
            <Outlet />
          </Container>
        </Box>
        <Paper sx={{ marginTop: 'calc(10% + 60px)', position: 'fixed', bottom: 0, width: '100%', height: '25px', justifyContent: 'center', zIndex: 1201 }} component="footer" square variant="outlined">
          <Stack direction="row" spacing={2}>
            <Box sx={{ textAlign: 'left', ml: '20px', mt: '1px' }}>
              <span style={{ textAlign: 'left', width: '100%' }}>
                <a href="https://www.dohwa.co.kr" target="_blank">
                  <img src={logo} />
                </a>
              </span>
            </Box>
            <Box>
              <Stack alignItems="flex-start" justifyContent="center" sx={{ height: '100%' }}>
                <span style={{ fontSize: '10px' }}>서울특별시 강남구 삼성로 438 (#06178) | 대표전화 02-6323-3000 | DOHWA Engineering Co., LTD. All RIGHTS RESERVED.</span>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </ThemeProvider>
  );
}

export default function Default() {
  return <DashboardContent />;
}
